<template>
  <div v-if="isOpen" class="k-dialog">
    <div ref="card" class="k-dialog__card">
      <div class="k-dialog__card_close">
        <slot :close="closeDialog" name="tail-action">
          <div class="icon-wrapper" @click="closeDialog">
            <div class="i-close-grey"></div>
          </div>
        </slot>
      </div>
      <div
        ref="header"
        class="k-dialog__header"
        @touchstart="onTouchStart"
        @touchend="onTouchEnd"
        @touchmove="onTouchMove"
      >
        <div class="k-dialog__slider">
          <div class="k-dialog__slider-stick"></div>
        </div>
        <div class="k-dialog__header_wrapper">
          <div class="k-dialog__header_title">
            <slot name="title">
              {{ title }}
            </slot>
          </div>
        </div>
      </div>
      <div ref="content" class="k-dialog__content">
        <slot :close="closeDialog"> asd </slot>
        <div v-if="$slots.footer" ref="footer" class="k-dialog__footer">
          <slot name="footer" :close="closeDialog"></slot>
        </div>
      </div>
    </div>
    <div ref="backdrop" class="k-dialog__backdrop" @click="closeDialog"></div>
  </div>
  <slot name="activator" :open="openDialog"></slot>
</template>

<script>
import useModalNav from "@/composables/useModalNavigation";
import { onMounted, ref, watch, nextTick } from "vue";

export default {
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
  },
  emits: ["update:isOpen"],
  setup(props, { emit }) {
    let currentOffset = 0;
    let actualHeight = 0;
    let cardStartOffset = 0;
    const closeThreshold = 100;
    const cardTopOffset = 12;
    const closeTimeout = 250;

    const header = ref(null);
    const backdrop = ref(null);
    const card = ref(null);
    const content = ref(null);

    useModalNav(props, emit, closeDialog);

    function openDialog() {
      emit("update:isOpen", true);
      requestAnimationFrame(() => {
        backdrop.value.style.opacity = 1;
      });
    }

    function closeDialog() {
      requestAnimationFrame(() => {
        card.value.style.transform = "translateY(120%)";
        backdrop.value.style.opacity = 0;
        currentOffset = 0;

        setTimeout(() => {
          emit("update:isOpen", false);
        }, closeTimeout);
      });
    }

    function initModalCard() {
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        const headerHeight = header.value.getBoundingClientRect().height;
        nextTick(() => {
          backdrop.value.style.opacity = 1;
          card.value.style.transform = "translateY(0)";
          content.value.style.maxHeight = `calc(100vh - ${
            headerHeight + cardTopOffset
          }px)`;

          actualHeight = card.value.getBoundingClientRect().height;
        });
      });
    }

    function onTouchStart(e) {
      cardStartOffset =
        window.innerHeight - e.touches[0].clientY - actualHeight;
      requestAnimationFrame(() => {
        card.value.style.transitionDuration = "0.03s";
      });
    }

    function onTouchMove(e) {
      currentOffset =
        (window.innerHeight -
          actualHeight -
          e.touches[0].clientY -
          cardStartOffset) *
        -1;
      if (currentOffset > 0) {
        card.value.style.transform = `translate(0, ${currentOffset}px)`;
      }
    }

    function onTouchEnd() {
      if (!card.value) {
        return;
      }
      card.value.style.transitionDuration = "0.25s";
      if (currentOffset > closeThreshold) {
        requestAnimationFrame(() => {
          closeDialog();
        });
      } else {
        card.value.style.transform = "translate(0, 0)";
      }
    }

    onMounted(() => {
      if (props.isOpen) {
        initModalCard();
      }
    });

    watch(
      () => props.isOpen,
      (val) => {
        if (val) {
          initModalCard();
        }
      },
      {
        immediate: true,
      }
    );

    return {
      header,
      backdrop,
      card,
      content,
      openDialog,
      closeDialog,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    };
  },
};
</script>

<style lang="scss" scoped>
.k-dialog {
  font-family: Roboto-Regular;
  position: fixed;
  width: 100vw;
  height: 100vh;
  z-index: 100;
  left: 0;
  top: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;

  &__backdrop {
    position: absolute;
    width: inherit;
    height: inherit;
    opacity: 0;
    transition: opacity 0.25s;
    background-color: rgba(0, 0, 0, 0.4);
  }

  &__card {
    width: 100vw;
    border-radius: 24px 24px 0 0;
    flex: 1;
    background-color: white;
    transform: translateY(150%);
    transition: transform 0.25s ease-out;
    overflow: hidden;
    z-index: 100;

    &_close {
      position: fixed;
      right: 20px;
      top: 32px;
      z-index: 100;
    }
  }

  &__header {
    position: relative;
    width: 100%;
    background-color: #fff;
    color: #4a4a4a;

    &_wrapper {
      padding: 16px;
      display: flex;
      justify-content: space-between;
    }

    &_title {
      font-family: Roboto-Bold;
      max-width: 300px;
      text-align: left;
      font-size: 20px;
      line-height: 24px;
    }
  }

  &__content {
    position: relative;
    overflow: scroll;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__footer {
    position: sticky;
    width: 100vw;
    bottom: 0;
    background-color: #fff;
  }

  &__slider {
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;

    &-stick {
      width: 32px;
      height: 4px;
      border-radius: 2px;
      background-color: rgba(0, 0, 0, 0.08);
    }
  }
}
</style>
